import { z } from 'zod';
import { AuditEventTypeSchema } from '../../schemas/subscription_audit';
import type { AuditQueryFilters } from '../../schemas/subscription_audit';

const id = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const utc = z.string().datetime();
export const SubscriptionAuditItemSchema = z.object({
  eventId: z.string().regex(/^(grant|revoke):[1-9]\d*$/),
  eventType: AuditEventTypeSchema,
  occurredAt: utc,
  environment: z.string().min(1),
  brand: z.string().min(1),
  memberAccountId: id,
  username: z.string().nullable(),
  sourceType: z.enum(['PURCHASE', 'MANUAL']).nullable(),
  productId: z.string().nullable(),
  durationUnit: z.string().nullable(),
  durationCount: z.number().int().positive().nullable(),
  revokedUnusedSeconds: z.string().regex(/^\d+$/).nullable(),
  grantId: id,
  operationId: id.nullable(),
  paymentOrderId: id.nullable(),
  validUntilBefore: utc.nullable(),
  validUntilAfter: utc.nullable(),
  actorType: z.enum(['SYSTEM', 'ADMIN', 'UNKNOWN']),
  actorAdminId: id.nullable(),
  actorName: z.string().nullable(),
  reason: z.string().nullable(),
  dataStatus: z.enum(['OK', 'PARTIAL']),
});
export type SubscriptionAuditItem = z.infer<typeof SubscriptionAuditItemSchema>;
export const SubscriptionAuditPageSchema = z.object({
  items: z.array(SubscriptionAuditItemSchema).max(50).refine(rows => new Set(rows.map(row => row.eventId)).size === rows.length, '重複事件'),
  nextCursor: z.string().min(1).nullable(),
  limit: z.literal(50),
  dataStatus: z.enum(['OK', 'PARTIAL']),
  generatedAt: utc,
  snapshot: z.string().min(1),
});
export type SubscriptionAuditPage = z.infer<typeof SubscriptionAuditPageSchema>;
export const auditTypeLabels = { PURCHASE: '支付訂閱', MANUAL_GRANT: '人工發放', MANUAL_REVOKE: '人工撤銷' } as const;

export function auditPath(brand: string, filters: AuditQueryFilters, page: { snapshot?: string; cursor?: string } = {}, exporting = false): string {
  if (!brand || brand === 'all') throw new Error('請選擇單一品牌');
  if (exporting && !page.snapshot) throw new Error('請先完成查詢再匯出');
  const params = new URLSearchParams({ brand });
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  if (page.snapshot) params.set('snapshot', page.snapshot);
  if (!exporting && page.cursor) params.set('cursor', page.cursor);
  return `/api/admin/subscriptions/audit-logs${exporting ? '/export' : ''}?${params}`;
}

/** A replaced task loses authority even when its request ignores AbortSignal. */
export class LatestAuditTask {
  private current: AbortController | null = null;
  replace() {
    this.cancel();
    const controller = new AbortController();
    this.current = controller;
    return { signal: controller.signal, isCurrent: () => this.current === controller && !controller.signal.aborted };
  }
  cancel() { this.current?.abort(); this.current = null; }
}

export async function completeAuditCsv(response: Response, signal: AbortSignal): Promise<Blob> {
  if (!response.ok) {
    let message = `匯出失敗 (${response.status})`;
    try { const result = await response.json(); if (typeof result.msg === 'string') message = result.msg; } catch { /* Preserve the HTTP error for a non-JSON response. */ }
    throw new Error(message);
  }
  if (!/^text\/csv(?:;|$)/i.test(response.headers.get('Content-Type') ?? '')) throw new Error('匯出格式不正確，請重新查詢後再試');
  const lengthText = response.headers.get('Content-Length');
  if (!lengthText || !/^[1-9]\d*$/.test(lengthText) || !Number.isSafeInteger(Number(lengthText))) throw new Error('無法確認匯出檔案完整性');
  const blob = await response.blob();
  if (signal.aborted) throw new DOMException('已取消匯出', 'AbortError');
  if (blob.size !== Number(lengthText)) throw new Error('匯出檔案不完整，請重新匯出');
  return blob;
}

export function saveAuditCsv(blob: Blob) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = `subscription-audit-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
  document.body.appendChild(link);
  try { link.click(); } finally { link.remove(); setTimeout(() => URL.revokeObjectURL(href), 1000); }
}
